import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { create } from 'zustand'

interface ImageStore {
  selected?: Image[] | null
  setPhoto: ({ photo, clearAll, isSingleImage }: { photo?: Image; clearAll?: boolean; isSingleImage?: boolean }) => void
  setInitialValue: (initialValue: unknown) => void
  reorderPhotos: (event: DragEndEvent) => void
}

export const useImageStore = create<ImageStore>((set, get) => {
  // Handler to toggle selection of a photo
  // If photo already exists in selection, remove it
  // If photo doesn't exist, add it to selection
  const setPhoto = ({
    photo,
    clearAll,
    isSingleImage,
  }: {
    photo?: Image
    clearAll?: boolean
    isSingleImage?: boolean
  }) => {
    if (clearAll) {
      return set({ selected: [] })
    }

    if (!photo) return

    const current = get().selected

    const exists = current?.find((p) => p.id === photo.id)

    if (exists) {
      set({ selected: current?.filter((p) => p.id !== photo.id) })
      return
    }

    if (isSingleImage) {
      set({ selected: [photo] })
      return
    }

    set({ selected: [...(current ?? []), photo] })
  }

  // Initialize the store with initial values
  // Handles both string (JSON) and direct object inputs
  const setInitialValue = (initialValue: unknown) => {
    if (typeof initialValue === 'string') {
      set({ selected: JSON.parse(initialValue) as Image[] })
      return
    }
    set({ selected: initialValue as Image[] })
  }

  // Handle reordering of photos during drag and drop
  // Uses dnd-kit's arrayMove utility to update array order
  const reorderPhotos = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id && over?.id) {
      const images = get().selected
      const oldIndex = images?.findIndex((image) => image.id === active.id) ?? 0
      const newIndex = images?.findIndex((image) => image.id === over.id) ?? 0
      return set({ selected: arrayMove(images ?? [], oldIndex, newIndex) })
    }
  }

  // Return the initial state and methods
  return {
    selected: [],
    setPhoto,
    reorderPhotos,
    setInitialValue,
  }
})
